// TailTrackLayer — MapLibre side-effect component for rendering a historical
// flight track from a tail-number lookup.
//
// Renders two map layers:
//   tail-lookup-track-line   — altitude-colored polyline (green=low → red=high)
//   tail-lookup-route-fixes  — filed route fix markers (circles)
//
// The parent controls visibility by passing `track` (non-null = show).
// When track is null or the component unmounts, sources are cleared.
//
// Altitude coloring: uses a MapLibre 'step' expression to bucket altitudes
// into 5 color bands (no altitude → gray, 0-50=green, 50-150=yellow,
// 150-250=orange, 250+=red). Colors use flight-level groupings typical
// for GA / turboprop operations.

import { useEffect, useRef } from 'react';
import { GeoJSONSource } from 'maplibre-gl';
import type maplibregl from 'maplibre-gl';
import { getMapInstance } from './MapView.js';
import type { FlightTrackResponse } from '../../api/flightaware/flight-track.js';

// ---- Source / layer IDs -----------------------------------------------------
const TRACK_SOURCE     = 'tail-lookup-track-source';
const ROUTE_SOURCE     = 'tail-lookup-route-source';
const TRACK_LINE_LAYER = 'tail-lookup-track-line';
const ROUTE_FIX_LAYER  = 'tail-lookup-route-fixes';
const ROUTE_LABEL_LAYER = 'tail-lookup-route-labels';

const EMPTY_LINE_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const EMPTY_POINT_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// ---- helpers ----------------------------------------------------------------

function buildTrackFC(track: FlightTrackResponse['track']): GeoJSON.FeatureCollection {
  if (!track || track.length < 2) return EMPTY_LINE_FC;

  // Build one LineString feature. We embed altitude as a property
  // on the feature so the layer can use it for coloring.
  // MapLibre 'line-gradient' only works on single-segment geojson, so
  // we use a simpler approach: split into segments, each with an avg altitude.
  const features: GeoJSON.Feature[] = [];
  for (let i = 0; i < track.length - 1; i++) {
    const a = track[i];
    const b = track[i + 1];
    const avgAlt = ((a.altitude ?? 0) + (b.altitude ?? 0)) / 2;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [a.lon, a.lat],
          [b.lon, b.lat],
        ],
      },
      properties: {
        altitude: avgAlt,
        groundspeed: a.groundspeed,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function buildRouteFC(fixes: FlightTrackResponse['routeFixes']): GeoJSON.FeatureCollection {
  if (!fixes || fixes.length === 0) return EMPTY_POINT_FC;
  return {
    type: 'FeatureCollection',
    features: fixes.map((f) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
      properties: { ident: f.ident },
    })),
  };
}

function ensureSources(map: maplibregl.Map) {
  if (!map.getSource(TRACK_SOURCE)) {
    map.addSource(TRACK_SOURCE, { type: 'geojson', data: EMPTY_LINE_FC });
    // Altitude-colored track polyline.
    // Step expression: 0-50 hundred-ft = green, 50-150 = yellow, 150-250 = orange, 250+ = red.
    // altitude property is in hundreds of feet (FL units from AeroAPI).
    map.addLayer({
      id: TRACK_LINE_LAYER,
      type: 'line',
      source: TRACK_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-width': 2.5,
        'line-color': [
          'step',
          ['coalesce', ['get', 'altitude'], 0],
          '#6b7280',    // default: no altitude data (gray)
          0.1,  '#22c55e',  // > 0 ft: green (low altitude)
          50,   '#eab308',  // > 5000 ft: yellow (mid-low)
          150,  '#f97316',  // > 15000 ft: orange (mid-high)
          250,  '#ef4444',  // > 25000 ft: red (high altitude)
        ] as maplibregl.DataDrivenPropertyValueSpecification<string>,
        'line-opacity': 0.85,
      },
    });
  }

  if (!map.getSource(ROUTE_SOURCE)) {
    map.addSource(ROUTE_SOURCE, { type: 'geojson', data: EMPTY_POINT_FC });
    // Filed route fix circles
    map.addLayer({
      id: ROUTE_FIX_LAYER,
      type: 'circle',
      source: ROUTE_SOURCE,
      paint: {
        'circle-radius': 4,
        'circle-color': '#38bdf8',  // sky blue
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#0f172a',
        'circle-opacity': 0.9,
      },
    });
    // Route fix labels
    map.addLayer({
      id: ROUTE_LABEL_LAYER,
      type: 'symbol',
      source: ROUTE_SOURCE,
      layout: {
        'text-field': ['get', 'ident'],
        'text-size': 10,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#bae6fd',
        'text-halo-color': '#0f172a',
        'text-halo-width': 1,
      },
    });
  }
}

function clearSources(map: maplibregl.Map) {
  const trackSrc = map.getSource(TRACK_SOURCE) as GeoJSONSource | undefined;
  const routeSrc = map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
  if (trackSrc) trackSrc.setData(EMPTY_LINE_FC);
  if (routeSrc) routeSrc.setData(EMPTY_POINT_FC);
}

function removeSources(map: maplibregl.Map) {
  for (const layer of [TRACK_LINE_LAYER, ROUTE_FIX_LAYER, ROUTE_LABEL_LAYER]) {
    if (map.getLayer(layer)) map.removeLayer(layer);
  }
  for (const source of [TRACK_SOURCE, ROUTE_SOURCE]) {
    if (map.getSource(source)) map.removeSource(source);
  }
}

// ---- component --------------------------------------------------------------

interface Props {
  /** Non-null = render this track. Null = clear and hide. */
  track: FlightTrackResponse | null;
}

export default function TailTrackLayer({ track }: Props) {
  const addedRef = useRef(false);

  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    if (!track) {
      // Clear track data but keep sources around for fast next-show.
      if (addedRef.current) clearSources(map);
      return;
    }

    ensureSources(map);
    addedRef.current = true;

    const trackFC = buildTrackFC(track.track);
    const routeFC = buildRouteFC(track.routeFixes);

    const trackSrc = map.getSource(TRACK_SOURCE) as GeoJSONSource | undefined;
    const routeSrc = map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
    if (trackSrc) trackSrc.setData(trackFC as any);
    if (routeSrc) routeSrc.setData(routeFC as any);

    // Fly to the track extent
    if (track.track.length >= 2) {
      const lats = track.track.map((p) => p.lat);
      const lons = track.track.map((p) => p.lon);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      try {
        map.fitBounds(
          [[minLon, minLat], [maxLon, maxLat]],
          { padding: 60, maxZoom: 10, duration: 800 },
        );
      } catch {
        // fitBounds can throw on degenerate extents — ignore.
      }
    }
  }, [track]);

  // Cleanup on unmount: remove sources + layers entirely.
  useEffect(() => {
    return () => {
      const map = getMapInstance();
      if (map && addedRef.current) {
        removeSources(map);
        addedRef.current = false;
      }
    };
  }, []);

  return null;
}
